import StudyUsersApi from "@/api/study-users";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
/**
 * Shared users store for QnA and AI summary experiences.
 * Uses /api/studies/{id}/users when a study context is available,
 * falling back to /api/study-users for cross-study lookups.
 */
export const useStudyUsers = defineStore("study-users", () => {
    const users = reactive({});
    const userStudyMap = reactive({});
    const isFetching = ref(false);
    const onLoad = (userId, user) => {
        if (users[userId]) {
            Object.assign(users[userId], user);
        }
        else {
            users[userId] = user;
        }
        return users[userId];
    };
    const putUser = (user) => onLoad(user.userId, user);
    const getUsers = (userIds, studyId) => {
        isFetching.value = true;
        return StudyUsersApi.getUsers(userIds, studyId)
            .then(({ users: fetchedUsers }) => {
            fetchedUsers.map((user) => onLoad(user.userId, user));
            return fetchedUsers;
        })
            .finally(() => {
            isFetching.value = false;
        });
    };
    const getUsersForStudy = (studyId) => {
        isFetching.value = true;
        return StudyUsersApi.getUsersForStudy(studyId)
            .then(({ users: fetchedUsers }) => {
            Object.assign(users, fetchedUsers.reduce((acc, user) => {
                acc[user.userId] = user;
                return acc;
            }, {}));
            return fetchedUsers;
        })
            .finally(() => {
            isFetching.value = false;
        });
    };
    const loadByUserId = (userId, studyId) => {
        const studyChanged = userStudyMap[userId] !== studyId;
        if (users[userId] && !studyChanged) {
            return users[userId];
        }
        userStudyMap[userId] = studyId;
        const placeholderUser = {
            userId,
            displayName: "",
            admin: false,
            thumbnails: {}
        };
        users[userId] = placeholderUser;
        StudyUsersApi.getUserById({ userId }, studyId).then((user) => {
            onLoad(user.userId, user);
        });
        // Return reactive proxy (users[userId]), not raw object (placeholderUser),
        // so Vue can track changes when onLoad updates the properties
        return users[userId];
    };
    const getUser = (userId, studyId) => loadByUserId(userId, studyId);
    return {
        users,
        isFetching,
        onLoad,
        putUser,
        getUsers,
        getUsersForStudy,
        getUser,
        loadByUserId
    };
});
